import { memo, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import type { PaytrieAPI } from '@paytrie/client'

import { getRecentPastWindows, HISTORY_DAYS } from '../maintenanceTimeline'
import type { MaintenanceFormatters } from '../useMaintenanceFormatters'
import {
  CARD_CLASS_NAME,
  MAINTENANCE_TITLE_CLASS_NAME,
  MUTED_TEXT_CLASS_NAME,
  SECTION_TITLE_CLASS_NAME,
} from './styles'

type PastWindowsProps = {
  windows: PaytrieAPI.MaintenanceWindow[]
  now: Date
  formatters: MaintenanceFormatters
}

export const PastWindows = memo(function PastWindows({
  windows,
  now,
  formatters,
}: PastWindowsProps) {
  const { t } = useTranslation()
  const recentWindows = useMemo(
    () => getRecentPastWindows({ windows, now }),
    [windows, now]
  )

  return (
    <section className="space-y-3">
      <h2 className={SECTION_TITLE_CLASS_NAME}>
        {t('maintenance.past.title')}
      </h2>
      {recentWindows.length === 0 ? (
        <p className={clsx('text-sm', MUTED_TEXT_CLASS_NAME)}>
          {t('maintenance.past.none', { count: HISTORY_DAYS })}
        </p>
      ) : (
        <ul
          className={clsx(
            CARD_CLASS_NAME,
            'divide-y divide-cloudDarker dark:divide-darkCharcoal'
          )}
        >
          {recentWindows.map((window) => (
            <li
              key={window.id}
              data-testid="maintenance-past-window"
              className="px-5 py-4"
            >
              <p className={MAINTENANCE_TITLE_CLASS_NAME}>{window.title}</p>
              <p className="mt-1 text-sm font-medium text-gravel dark:text-snow">
                {formatters.formatSchedule(window)}
              </p>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
})
